import { UserOutlined } from "@ant-design/icons"
import { Avatar, Button, Col, Flex, Input, Row, Select, Tag, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { CopyableLink } from "../../../../components/CopyableLink.tsx"
import {
  formatUsPhone,
  PhoneInput
} from "../../../../components/PhoneInputs/PhoneInput.tsx"
import { RoutesPaths } from "../../../../constants/routes.ts"
import {
  invalidPhoneMessage,
  isValidPhone
} from "../../../../utils/validation/validate-phone.ts"
import { type CustomerSearchResult } from "../../../CustomersPage/types/customer.types.ts"
import { type LeadsFilterOptions } from "../../../Leads/redux/leads.types.ts"
import { type LeadFormDataCustomer } from "../../LeadAddPage/components/lead-form-data.ts"
import { type Lead } from "../../types/lead.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import {
  type LeadPatchSender,
  type LeadWorkspacePatch,
  pickChangedFields
} from "../lead-workspace-patch.ts"
import { LeadChangeCustomerModal } from "./LeadChangeCustomerModal.tsx"
import { LeadOverviewField } from "./LeadOverviewField.tsx"
import { OverviewCardTitle } from "./OverviewCardIcon.tsx"
import {
  customerFromSearchResult,
  isLinkedCustomer,
  leadCustomerName,
  leadCustomerValues
} from "./lead-overview-customer.ts"
import { initialsOf } from "./overview-initials.ts"

const { Text } = Typography

interface CustomerCardValues {
  customer: LeadFormDataCustomer
  preferred_contact_method: string | null
}

interface LeadOverviewCustomerCardProps {
  lead: Lead
  filterOptions: LeadsFilterOptions
  canEdit?: boolean
  saving?: boolean
  onPatch: LeadPatchSender
}

interface CustomerPhoneFieldProps {
  label: string
  value?: string | null
  saving: boolean
  errorId: string
  onChange: (value: string | null) => void
}

function CustomerPhoneField({
  label,
  value,
  saving,
  errorId,
  onChange
}: CustomerPhoneFieldProps) {
  const invalid = !isValidPhone(value)

  return (
    <LeadOverviewField label={label}>
      <PhoneInput
        disabled={saving}
        aria-label={label}
        aria-describedby={invalid ? errorId : undefined}
        aria-invalid={invalid}
        status={invalid ? "error" : undefined}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value || null)}
      />
      {invalid && (
        <Text id={errorId} type={"danger"} role={"alert"}>
          {invalidPhoneMessage}
        </Text>
      )}
    </LeadOverviewField>
  )
}

function LeadOverviewCustomerCard({
  lead,
  filterOptions,
  canEdit,
  saving,
  onPatch
}: LeadOverviewCustomerCardProps) {
  const { t } = useTranslation()
  const [changing, setChanging] = useState(false)

  const customer = leadCustomerValues(lead)
  const name = leadCustomerName(customer)
  const linked = isLinkedCustomer(lead)
  const linkedCustomerId = lead?.customer?.id ?? null

  const values: CustomerCardValues = {
    customer,
    preferred_contact_method: lead?.preferred_contact_method ?? null
  }

  const contactMethod = filterOptions?.contact_method?.find(
    (option) => option?.value === lead?.preferred_contact_method
  )

  const changeCustomer = (next: CustomerSearchResult) =>
    onPatch({
      customer_id: next.id ?? null,
      customer: customerFromSearchResult(next)
    }).then((saved) => {
      if (saved) setChanging(false)
      return saved
    })

  // The nested customer write merges per field server-side, so the card follows
  // the same changed-fields discipline as every other card rather than echoing
  // a record whose derived keys it cannot legally send back.
  const saveCustomer = (changed: Partial<CustomerCardValues>) => {
    const patch: LeadWorkspacePatch = { ...changed }

    if (changed.customer) {
      const edited = pickChangedFields(customer, changed.customer)
      if (Object.keys(edited).length) {
        patch.customer = edited
        if (linkedCustomerId) patch.customer_id = linkedCustomerId
      } else delete patch.customer
    }

    return onPatch(patch)
  }

  return (
    <>
      <WorkspaceCard<CustomerCardValues>
        cardId={"customer"}
        title={
          <OverviewCardTitle icon={<UserOutlined />} accent={"teal"}>
            {t("leadWorkspace.cards.customer.title")}
          </OverviewCardTitle>
        }
        ariaTitle={t("leadWorkspace.cards.customer.title")}
        className={"h-full"}
        canEdit={canEdit}
        values={values}
        validate={({ customer }) =>
          isValidPhone(customer.phone) && isValidPhone(customer.phone_2)
        }
        onSave={saveCustomer}
        extra={
          <Flex gap={"small"} align={"center"}>
            {/* Only a linked record has a page to open; a draft customer has no id. */}
            {linkedCustomerId && (
              <Link to={RoutesPaths.customers.view(linkedCustomerId)}>
                <Button size={"small"}>
                  {t("leadWorkspace.cards.customer.viewFull")}
                </Button>
              </Link>
            )}
            {canEdit && (
              <Button size={"small"} onClick={() => setChanging(true)}>
                {t("leadWorkspace.cards.customer.change")}
              </Button>
            )}
          </Flex>
        }
        renderEdit={({ values, setValues, saving }) => (
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={12}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.customer.firstName")}
              >
                <Input
                  autoFocus
                  disabled={saving}
                  aria-label={t("leadWorkspace.cards.customer.firstName")}
                  value={values.customer.first_name ?? ""}
                  onChange={(event) =>
                    setValues({
                      customer: {
                        ...values.customer,
                        first_name: event.target.value || null
                      }
                    })
                  }
                />
              </LeadOverviewField>
            </Col>
            <Col xs={24} sm={12}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.customer.lastName")}
              >
                <Input
                  disabled={saving}
                  aria-label={t("leadWorkspace.cards.customer.lastName")}
                  value={values.customer.last_name ?? ""}
                  onChange={(event) =>
                    setValues({
                      customer: {
                        ...values.customer,
                        last_name: event.target.value || null
                      }
                    })
                  }
                />
              </LeadOverviewField>
            </Col>
            <Col xs={24} sm={12}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.customer.email")}
              >
                <Input
                  disabled={saving}
                  aria-label={t("leadWorkspace.cards.customer.email")}
                  value={values.customer.email ?? ""}
                  onChange={(event) =>
                    setValues({
                      customer: {
                        ...values.customer,
                        email: event.target.value || null
                      }
                    })
                  }
                />
              </LeadOverviewField>
            </Col>
            <Col xs={24} sm={12}>
              <CustomerPhoneField
                label={t("leadWorkspace.cards.customer.primaryPhone")}
                value={values.customer.phone}
                saving={saving}
                errorId={"lead-customer-primary-phone-error"}
                onChange={(phone) =>
                  setValues({
                    customer: { ...values.customer, phone }
                  })
                }
              />
            </Col>
            <Col xs={24} sm={12}>
              <CustomerPhoneField
                label={t("leadWorkspace.cards.customer.secondaryPhone")}
                value={values.customer.phone_2}
                saving={saving}
                errorId={"lead-customer-secondary-phone-error"}
                onChange={(phone_2) =>
                  setValues({
                    customer: { ...values.customer, phone_2 }
                  })
                }
              />
            </Col>
            <Col xs={24} sm={12}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.customer.contactMethod")}
              >
                <Select
                  allowClear
                  className={"w-full"}
                  disabled={saving}
                  aria-label={t("leadWorkspace.cards.customer.contactMethod")}
                  options={filterOptions?.contact_method ?? []}
                  value={values.preferred_contact_method ?? undefined}
                  onChange={(value) =>
                    setValues({ preferred_contact_method: value ?? null })
                  }
                />
              </LeadOverviewField>
            </Col>
          </Row>
        )}
      >
        <Flex vertical gap={22}>
          {/* Identity: who this is, in one scannable line — no field label
              of its own, the same way a person's name never needs a caption
              on a profile card. */}
          <Flex gap={12} align={"center"} wrap>
            {!!name && (
              <Avatar className={"bg-primary shrink-0"} size={48}>
                {initialsOf(name)}
              </Avatar>
            )}
            <Text
              className={"min-w-0 truncate text-[21px] font-semibold leading-tight"}
            >
              {name}
            </Text>
            <Tag
              color={linked ? "green" : "default"}
              className={"shrink-0"}
              data-testid={"lead-overview-customer-link"}
            >
              {linked
                ? t("leadWorkspace.cards.customer.primary")
                : t("leadWorkspace.cards.customer.unlinked")}
            </Tag>
          </Flex>

          {/* Contact: a compact two-column grid, muted labels over prominent
              values — Email/Secondary Phone on the left, Primary Phone/
              Preferred Contact Method on the right. */}
          <Row gutter={[48, 18]}>
            <Col xs={24} sm={12}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.customer.email")}
                value={customer.email}
              >
                <span className={"text-sm font-medium"}>
                  <CopyableLink type={"email"} href={customer.email!}>
                    {customer.email}
                  </CopyableLink>
                </span>
              </LeadOverviewField>
            </Col>
            <Col xs={24} sm={12}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.customer.primaryPhone")}
                value={customer.phone}
              >
                <span className={"text-sm font-medium"}>
                  <CopyableLink type={"phone"} href={customer.phone!}>
                    {formatUsPhone(customer.phone)}
                  </CopyableLink>
                </span>
              </LeadOverviewField>
            </Col>
            <Col xs={24} sm={12}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.customer.secondaryPhone")}
                value={customer.phone_2}
              >
                <span className={"text-sm font-medium"}>
                  <CopyableLink type={"phone"} href={customer.phone_2!}>
                    {formatUsPhone(customer.phone_2)}
                  </CopyableLink>
                </span>
              </LeadOverviewField>
            </Col>
            <Col xs={24} sm={12}>
              <LeadOverviewField
                label={t("leadWorkspace.cards.customer.contactMethod")}
                value={contactMethod?.label ?? lead?.preferred_contact_method}
              />
            </Col>
          </Row>
        </Flex>
      </WorkspaceCard>

      <LeadChangeCustomerModal
        open={changing}
        saving={saving}
        onCancel={() => setChanging(false)}
        onConfirm={changeCustomer}
      />
    </>
  )
}

export { type CustomerCardValues, LeadOverviewCustomerCard }
